import fetchDataService from "./fetchDataService";

const getBaseApiUrl = () => process.env.REACT_APP_API_URL || "/api";

export const ECOMMERCE_ENDPOINTS = {
  // Administrativos (sesión ERP cifrada)
  CREATE_STORE: "/ecommerce/create-store",
  UPDATE_STORE: "/ecommerce/update-store",
  GET_STORE: "/ecommerce/get-store",
  GET_STORES_BY_COMPANY: "/ecommerce/get-stores-by-company",
  SET_STORE_ITEMS: "/ecommerce/set-store-items",
  GET_STORE_ITEMS: "/ecommerce/get-store-items",
  GET_ORDERS: "/ecommerce/get-orders",
  UPDATE_ORDER_STATUS: "/ecommerce/update-order-status",
  RESOLVE_ORDER_CUSTOMER: "/ecommerce/resolve-order-customer",

  // Públicos del Storefront (POST JSON estándar bajo /api)
  PUBLIC_CATALOG: "/public/ecommerce/catalog",
  PUBLIC_CHECK_DOCUMENT: "/public/ecommerce/check-document",
  PUBLIC_CREATE_ORDER: "/public/ecommerce/orders",
  PUBLIC_TRACKING: "/public/ecommerce/tracking",
} as const;

const adminRequest = async (requestPromise: Promise<any>, errorMessage: string) => {
  try {
    const response = await requestPromise;
    if (response?.error || (response?.status && response.status !== 200)) {
      throw new Error(response.message || response.error || errorMessage);
    }
    return response;
  } catch (err: any) {
    return { error: true, status: err?.status || 230, message: err?.message || errorMessage };
  }
};

const publicPost = async (endpoint: string, payload: Record<string, any>, errorMessage: string) => {
  try {
    const res = await fetch(`${getBaseApiUrl()}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.status === 429) {
      const data = await res.json().catch(() => ({}));
      return {
        error: true,
        status: 429,
        message: data?.message || "Demasiadas solicitudes. Intenta nuevamente en unos segundos.",
      };
    }

    const data = await res.json();
    const isSuccess = res.ok && (data?.status === 200 || data?.success === true || data?.matched !== undefined || data?.exists !== undefined);

    if (!isSuccess) {
      return {
        error: true,
        status: data?.status || res.status || 220,
        message: data?.message || errorMessage,
      };
    }

    return data;
  } catch (err: any) {
    return {
      error: true,
      status: 230,
      message: errorMessage,
    };
  }
};

// ──────────────────────────────────────────────────────────
// 1. Operaciones Administrativas (Storage ERP)
// ──────────────────────────────────────────────────────────

const createStore = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.CREATE_STORE, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "No fue posible crear la tienda online"
  );
};

const updateStore = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.UPDATE_STORE, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "No fue posible actualizar la tienda online"
  );
};

const getStoresByCompany = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.GET_STORES_BY_COMPANY, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "Error al consultar tiendas online"
  );
};

const setStoreItems = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.SET_STORE_ITEMS, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "Error al actualizar publicación de elementos"
  );
};

const getStoreItems = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.GET_STORE_ITEMS, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "Error al consultar items de la tienda"
  );
};

const getOrders = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.GET_ORDERS, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "Error al consultar pedidos web"
  );
};

const updateOrderStatus = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.UPDATE_ORDER_STATUS, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "Error al actualizar el estado del pedido"
  );
};

const resolveOrderCustomer = async (tokenData: any, requestData: any) => {
  return await adminRequest(
    fetchDataService.post(ECOMMERCE_ENDPOINTS.RESOLVE_ORDER_CUSTOMER, tokenData?.key, {
      ...requestData,
      csrfToken: tokenData?.token?.uid,
    }),
    "Error al registrar o vincular cliente del pedido"
  );
};

// ──────────────────────────────────────────────────────────
// 2. Operaciones Públicas del Storefront
// ──────────────────────────────────────────────────────────

const getPublicCatalog = async (params: {
  slug?: string | null;
  store_id?: number | null;
  search?: string | null;
  limit?: number | null;
  offset?: number | null;
}) => {
  return await publicPost(
    ECOMMERCE_ENDPOINTS.PUBLIC_CATALOG,
    params,
    "Error de conexión al cargar el catálogo."
  );
};

const checkPublicDocument = async (params: {
  slug?: string | null;
  company_id?: number | null;
  document_number: string;
  document_type?: string | null;
}) => {
  return await publicPost(
    ECOMMERCE_ENDPOINTS.PUBLIC_CHECK_DOCUMENT,
    params,
    "No fue posible verificar el documento."
  );
};

const createPublicOrder = async (payload: Record<string, any>) => {
  return await publicPost(
    ECOMMERCE_ENDPOINTS.PUBLIC_CREATE_ORDER,
    payload,
    "Error de conexión al enviar el pedido."
  );
};

const getPublicOrderTracking = async (params: {
  tracking_token: string;
  store_slug?: string | null;
}) => {
  return await publicPost(
    ECOMMERCE_ENDPOINTS.PUBLIC_TRACKING,
    params,
    "Error de red al consultar el estado del pedido."
  );
};

const ecommerceService = {
  createStore,
  updateStore,
  getStoresByCompany,
  setStoreItems,
  getStoreItems,
  getOrders,
  updateOrderStatus,
  resolveOrderCustomer,
  getPublicCatalog,
  checkPublicDocument,
  createPublicOrder,
  getPublicOrderTracking,
};

export default ecommerceService;
